
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, FileText, Trophy, Building2, User, Calendar } from "lucide-react";
import { formatCurrency } from "@/lib/utils";

interface Deal {
  id: string;
  title: string;
  value: number;
  stage?: { name: string; color: string } | null;
  contact?: { first_name: string; last_name: string; email: string } | null;
  organization?: { name: string; company_id: string } | null;
  assigned_staff?: { first_name: string; last_name: string; email: string } | null;
  assigned_partner?: { first_name: string; last_name: string; email: string } | null;
  created_at: string;
  total_cameras?: number | null;
  total_images?: number | null;
  status: string;
  archived?: boolean | null;
}

interface ContactDealsListProps {
  deals: Deal[];
  isLoading: boolean;
  title?: string;
  showAssignee?: boolean;
  showContact?: boolean;
}

export const ContactDealsList = ({ 
  deals, 
  isLoading, 
  title = "Deals",
  showAssignee = false,
  showContact = false 
}: ContactDealsListProps) => {
  if (isLoading) {
    return (
      <div className="p-4 text-center text-slate-600">
        Loading deals...
      </div>
    );
  }

  // Group deals by stage/status (exclude archived from active opportunities/proposals)
  const opportunities = deals.filter(deal =>
    !deal.archived && (deal.stage?.name === 'Opportunity' || deal.status === 'draft')
  );
  const proposals = deals.filter(deal =>
    !deal.archived && (deal.stage?.name === 'Proposal' || deal.status === 'proposal')
  );
  const wonDeals = deals.filter(deal => 
    deal.stage?.name === 'Won' || deal.status === 'won'
  );

  const DealMiniCard = ({ deal }: { deal: Deal }) => (
    <Card className="bg-white border-slate-200 rounded-lg shadow-sm hover:shadow-md transition-all">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h4 className="font-semibold text-slate-800 text-sm mb-1 truncate">
              {deal.title}
            </h4>
            <div className="text-lg font-bold text-teal-600">
              {formatCurrency(deal.value || 0)}
            </div>
          </div>
          {deal.stage && (
            <Badge 
              className="text-xs"
              style={{ 
                backgroundColor: `${deal.stage.color}20`, 
                color: deal.stage.color,
                border: `1px solid ${deal.stage.color}40`
              }}
            >
              {deal.stage.name}
            </Badge>
          )}
        </div>
        
        <div className="space-y-1 text-xs text-slate-600">
          {deal.total_cameras && (
            <div className="flex items-center gap-1">
              <span className="font-medium">{deal.total_cameras}</span>
              <span>cameras</span>
              {deal.total_images && (
                <>
                  <span>•</span>
                  <span>{deal.total_images.toLocaleString()} images</span>
                </>
              )}
            </div>
          )}
          
          {showContact && deal.contact && (
            <div className="flex items-center gap-1">
              <User className="h-3 w-3" />
              <span>{deal.contact.first_name} {deal.contact.last_name}</span>
            </div>
          )}
          
          {showAssignee && (deal.assigned_staff || deal.assigned_partner) && (
            <div className="flex items-center gap-1">
              <User className="h-3 w-3" />
              <span>
                {deal.assigned_staff ? 
                  `${deal.assigned_staff.first_name} ${deal.assigned_staff.last_name}` :
                  `${deal.assigned_partner?.first_name} ${deal.assigned_partner?.last_name}`
                } ({deal.assigned_staff ? 'Staff' : 'Partner'})
              </span>
            </div>
          )}
          
          {deal.organization && (
            <div className="flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              <span className="truncate">{deal.organization.name}</span>
            </div>
          )}
          
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            <span>{new Date(deal.created_at).toLocaleDateString()}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const EmptyState = ({ message }: { message: string }) => (
    <div className="p-6 text-center text-slate-500">
      <p className="text-sm">{message}</p>
    </div>
  );

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-slate-800 flex items-center gap-2">
        <FileText className="h-4 w-4" />
        {title} ({deals.length})
      </h3>
      
      <Tabs defaultValue="opportunities" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="opportunities" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Opportunities ({opportunities.length})
          </TabsTrigger>
          <TabsTrigger value="proposals" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Proposals ({proposals.length})
          </TabsTrigger>
          <TabsTrigger value="won" className="flex items-center gap-2">
            <Trophy className="h-4 w-4" />
            Won ({wonDeals.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="opportunities" className="mt-4">
          {opportunities.length > 0 ? (
            <div className="grid grid-cols-1 gap-3">
              {opportunities.map((deal) => (
                <DealMiniCard key={deal.id} deal={deal} />
              ))}
            </div>
          ) : (
            <EmptyState message="No opportunities found" />
          )}
        </TabsContent>
        
        <TabsContent value="proposals" className="mt-4">
          {proposals.length > 0 ? (
            <div className="grid grid-cols-1 gap-3">
              {proposals.map((deal) => (
                <DealMiniCard key={deal.id} deal={deal} />
              ))}
            </div>
          ) : (
            <EmptyState message="No proposals found" />
          )}
        </TabsContent>
        
        <TabsContent value="won" className="mt-4">
          {wonDeals.length > 0 ? (
            <div className="grid grid-cols-1 gap-3">
              {wonDeals.map((deal) => (
                <DealMiniCard key={deal.id} deal={deal} />
              ))}
            </div>
          ) : (
            <EmptyState message="No won deals found" />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
